import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'

export interface GoalEntry {
  id: string
  date: string
  amount: number
  timestamp: number
}

export interface Goal {
  id: string
  userId: string
  name: string
  type: 'debt' | 'savings'
  targetAmount: number
  currentAmount: number
  startDate: string
  endDate: string
  entries: GoalEntry[]
}

export function useGoals() {
  const { user } = useAuth()
  const [goals, setGoals] = useState<Goal[]>([])

  useEffect(() => {
    if (user) {
      loadGoals()
    }
  }, [user])

  const loadGoals = () => {
    const allGoals = JSON.parse(localStorage.getItem('goals') || '[]')
    const userGoals = allGoals.filter((g: Goal) => g.userId === user?.id)
    setGoals(userGoals)
  }

  const createGoal = (
    goalData: Omit<Goal, 'id' | 'userId' | 'currentAmount' | 'entries'>,
  ) => {
    const newGoal: Goal = {
      ...goalData,
      id: Date.now().toString(),
      userId: user!.id,
      currentAmount: 0,
      entries: [],
    }

    const allGoals = JSON.parse(localStorage.getItem('goals') || '[]')
    allGoals.push(newGoal)
    localStorage.setItem('goals', JSON.stringify(allGoals))
    loadGoals()
  }

  const updateGoal = (
    goalId: string,
    updates: Partial<Pick<Goal, 'name' | 'targetAmount' | 'endDate' | 'type'>>,
  ) => {
    const allGoals = JSON.parse(localStorage.getItem('goals') || '[]')
    const goalIndex = allGoals.findIndex((g: Goal) => g.id === goalId)

    if (goalIndex !== -1) {
      allGoals[goalIndex] = {
        ...allGoals[goalIndex],
        ...updates,
      }
      localStorage.setItem('goals', JSON.stringify(allGoals))
      loadGoals()
    }
  }

  const addEntry = (goalId: string, amount: number) => {
    const allGoals = JSON.parse(localStorage.getItem('goals') || '[]')
    const goalIndex = allGoals.findIndex((g: Goal) => g.id === goalId)

    if (goalIndex !== -1) {
      const today = new Date().toISOString().split('T')[0]
      const goal = allGoals[goalIndex]

      const newEntry: GoalEntry = {
        id: Date.now().toString(),
        date: today,
        amount,
        timestamp: Date.now(),
      }

      goal.entries.push(newEntry)

      goal.currentAmount = goal.entries.reduce(
        (sum: number, e: GoalEntry) => sum + e.amount,
        0,
      )

      allGoals[goalIndex] = goal
      localStorage.setItem('goals', JSON.stringify(allGoals))
      loadGoals()
    }
  }

  const deleteGoal = (goalId: string) => {
    const allGoals = JSON.parse(localStorage.getItem('goals') || '[]')
    const filtered = allGoals.filter((g: Goal) => g.id !== goalId)
    localStorage.setItem('goals', JSON.stringify(filtered))
    loadGoals()
  }

  const calculateDailyTarget = (goal: Goal): number => {
    const remaining = goal.targetAmount - goal.currentAmount
    const today = new Date()
    const end = new Date(goal.endDate)
    const daysLeft = Math.ceil(
      (end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    )

    if (daysLeft <= 0) return remaining
    return remaining / daysLeft
  }

  const calculateProgress = (goal: Goal): number => {
    return Math.min((goal.currentAmount / goal.targetAmount) * 100, 100)
  }

  const getTodayTotal = (goal: Goal): number => {
    const today = new Date().toISOString().split('T')[0]
    const todayEntries = goal.entries.filter((e) => e.date === today)
    return todayEntries.reduce((sum, e) => sum + e.amount, 0)
  }

  const getTodayEntries = (goal: Goal): GoalEntry[] => {
    const today = new Date().toISOString().split('T')[0]
    return goal.entries
      .filter((e) => e.date === today)
      .sort((a, b) => b.timestamp - a.timestamp)
  }

  return {
    goals,
    createGoal,
    updateGoal,
    addEntry,
    deleteGoal,
    calculateDailyTarget,
    calculateProgress,
    getTodayTotal,
    getTodayEntries,
  }
}
